import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { homePathFor } from "@/lib/roles";

const LINKS = [
  { label: "SERVICES", href: "#services" },
  { label: "PROCESS", href: "#process" },
  { label: "COVERAGE", href: "#coverage" },
  { label: "COMPLIANCE", href: "#compliance" },
  { label: "PRICING", href: "/pricing" },
  { label: "CONTACT", href: "/contact" },
];

const HOW = [
  { label: "FOR BUSINESSES", href: "/how-it-works/business", id: "business" },
  { label: "FOR INDIVIDUALS", href: "/how-it-works/individuals", id: "individuals" },
];

const HowMenu = ({ go }) => (
  <div className="group relative">
    <button data-testid="nav-how-it-works-trigger" className="flex items-center gap-1 whitespace-nowrap font-mono-label text-[10px] font-semibold text-oath-secondary transition-colors hover:text-oath-accent group-focus-within:text-oath-accent">
      HOW IT WORKS <ChevronDown className="h-3 w-3 transition-transform group-hover:rotate-180" />
    </button>
    <div className="invisible absolute left-1/2 top-full z-10 -translate-x-1/2 pt-3 opacity-0 transition-opacity group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
      <div className="w-48 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-lg shadow-black/5">
        {HOW.map((h) => (
          <button key={h.id} onClick={() => go(h.href)} data-testid={`nav-how-${h.id}-link`}
            className="block w-full rounded-xl px-3 py-2.5 text-left font-mono-label text-[10px] font-semibold text-oath-ink transition-colors hover:bg-oath-bg hover:text-oath-accent">{h.label}</button>
        ))}
      </div>
    </div>
  </div>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goToSection = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
    } else if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="fixed inset-x-0 top-4 z-50 flex flex-col items-center px-4">
      <nav
        data-testid="main-navbar"
        className={`flex w-full max-w-[1080px] items-center justify-between gap-4 rounded-full border px-4 py-2.5 transition-all duration-300 ${
          scrolled
            ? "border-gray-200/70 bg-white/90 shadow-lg shadow-black/5 backdrop-blur-md"
            : "border-gray-200/50 bg-white/70 backdrop-blur-sm"
        }`}
      >
        <Logo />

        <div className="hidden items-center gap-6 xl:flex">
          <HowMenu go={goToSection} />
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              onClick={() => goToSection(l.href)}
              className="whitespace-nowrap font-mono-label text-[10px] font-semibold text-oath-secondary transition-colors hover:text-oath-accent"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex shrink-0 items-center gap-2">
        <Link
          to={user ? homePathFor(user) : "/signin"}
          data-testid="nav-signin-button"
          className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-oath-ink px-4 py-2 font-mono-label text-[10px] font-semibold text-white transition-colors hover:bg-black"
        >
          <span className="hidden sm:inline">{user ? "OPEN CONSOLE" : "OPERATOR SIGN IN"}</span>
          <span className="sm:hidden">{user ? "CONSOLE" : "SIGN IN"}</span>
          <span className="grid h-4 w-4 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
            <ArrowRight className="h-2.5 w-2.5" strokeWidth={2.5} />
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          className="grid h-8 w-8 place-items-center rounded-full border border-gray-200 text-oath-ink transition-colors hover:bg-gray-100 xl:hidden"
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
        </div>
      </nav>
      {open && (
        <div data-testid="nav-mobile-menu" className="mt-2 w-full max-w-[1080px] animate-fade-up rounded-3xl border border-gray-200 bg-white/95 p-2 shadow-lg shadow-black/5 backdrop-blur-md xl:hidden">
          {HOW.map((h) => (
            <button key={h.id} onClick={() => goToSection(h.href)} data-testid={`nav-mobile-how-${h.id}-link`}
              className="block w-full rounded-2xl px-4 py-3 text-left font-mono-label text-[11px] font-semibold text-oath-ink transition-colors hover:bg-oath-bg">
              HOW IT WORKS · {h.label}
            </button>
          ))}
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
              onClick={() => goToSection(l.href)}
              className="block w-full rounded-2xl px-4 py-3 text-left font-mono-label text-[11px] font-semibold text-oath-ink transition-colors hover:bg-oath-bg"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Navbar;
